import type { FleetTruckLocation } from '@numeo/types'
import type { ELDType } from '@numeo/types-core'
import { formatTimeAgo } from '@/utils/format-relative-time'
import { hasConnectedEld, hasNoEldLink } from '@/pages/dashboard/pages/profiles/pages/trucks/utils/manual-mode'

/**
 * Can this truck's position be read at all: it has an ELD, or it has no ELD link of any kind and the company places such trucks by
 * the seated driver's phone (ADR-059). The one rule behind every "skip the location read for a truck with no ELD" check.
 */
export const hasTruckLocationSource = (truck: Parameters<typeof hasConnectedEld>[0], phoneTruckLocation: boolean): boolean =>
    hasConnectedEld(truck) || (phoneTruckLocation && !!truck && hasNoEldLink(truck))

/** A fleet position placed by an ELD provider: the maps that draw one legend, colour and name per provider leave out a phone-placed truck. */
export const isPlacedByEld = (truck: FleetTruckLocation): truck is FleetTruckLocation & { eldType: ELDType } => truck.eldType !== undefined

/** What a list shows beside a phone-placed truck's place name: the phone is its source and the fix may be hours old. Absent for an ELD position, which is live. */
export const phoneLocationNote = (position: Pick<FleetTruckLocation, 'locationSource' | 'timestamp'>): string | undefined =>
    position.locationSource === 'phone' ? `Phone · ${formatTimeAgo(position.timestamp) || 'unknown'}` : undefined

/** A place name with its phone note after it ("Atlanta, GA · Phone · 4h ago"), for a text that has no room for a separate badge. */
export const locationWithNote = (place: string | undefined, position: Pick<FleetTruckLocation, 'locationSource' | 'timestamp'>): string | undefined => {
    const note = phoneLocationNote(position)
    return place && note ? `${place} · ${note}` : place
}
